import { flashElement } from "@/components/ui/deep-link-focus/flashElement";

/**
 * Per-window registry of UI targets Floating Ani can show (`fe_show` ref paths).
 *
 * Tabs, sections and buttons register through `<Tabs aniNav>`, `<AniNavTarget>` or `useAniNavTarget`.
 * Paths nest through `AniNavScope` (e.g. `holdings/top_holdings`), keyed by OS window id.
 */

export type AniNavTargetKind = "tab" | "section" | "action";

export type AniNavEntry = {
  path: string;
  kind: AniNavTargetKind;
  label: string;
  getElement: () => HTMLElement | null;
  /** Tabs switch, actions click. Sections only scroll and flash. */
  activate?: () => void;
  isActive?: () => boolean;
};

export type AniNavTargetSummary = {
  ref: string;
  label: string;
  kind: AniNavTargetKind;
  active?: boolean;
};

export type AniNavShowResult =
  | { ok: true; labels: string[] }
  | { ok: false; missing: string; available: AniNavTargetSummary[] };

/** Scope used outside OS windows (classic full-page routes). */
export const PAGE_NAV_SCOPE = "page";

const MAX_LISTED_TARGETS = 40;
const WAIT_TIMEOUT_MS = 4000;
const WAIT_INTERVAL_MS = 90;

class AniNavRegistry {
  private scopes = new Map<string, Map<string, AniNavEntry>>();

  register(scope: string, entry: AniNavEntry): () => void {
    let entries = this.scopes.get(scope);
    if (!entries) {
      entries = new Map();
      this.scopes.set(scope, entries);
    }
    entries.set(entry.path, entry);
    return () => {
      const current = this.scopes.get(scope);
      if (current?.get(entry.path) !== entry) {
        return;
      }
      current.delete(entry.path);
      if (current.size === 0) {
        this.scopes.delete(scope);
      }
    };
  }

  get(scope: string, path: string): AniNavEntry | undefined {
    return this.scopes.get(scope)?.get(path);
  }

  list(scope: string): AniNavTargetSummary[] {
    const entries = Array.from(this.scopes.get(scope)?.values() ?? []);
    return entries.slice(0, MAX_LISTED_TARGETS).map((entry) => ({
      ref: entry.path,
      label: entry.label,
      kind: entry.kind,
      ...(entry.isActive ? { active: entry.isActive() } : {}),
    }));
  }

  clear(): void {
    this.scopes.clear();
  }
}

export const aniNavRegistry = new AniNavRegistry();

async function waitForEntry(scope: string, path: string): Promise<AniNavEntry | undefined> {
  const deadline = Date.now() + WAIT_TIMEOUT_MS;
  for (;;) {
    const entry = aniNavRegistry.get(scope, path);
    if (entry || Date.now() >= deadline) {
      return entry;
    }
    await new Promise((resolve) => window.setTimeout(resolve, WAIT_INTERVAL_MS));
  }
}

/**
 * Walk ``path`` segment by segment in ``scope``: activate each tab on the way (so nested sections mount),
 * then flash the leaf (and click it when it is an action).
 */
export async function showAniNavPath(scope: string, path: string): Promise<AniNavShowResult> {
  const segments = path.split("/").map((segment) => segment.trim()).filter(Boolean);
  const labels: string[] = [];
  let current = "";
  for (const [index, segment] of segments.entries()) {
    current = current ? `${current}/${segment}` : segment;
    const entry = await waitForEntry(scope, current);
    if (!entry) {
      return { ok: false, missing: current, available: aniNavRegistry.list(scope) };
    }
    labels.push(entry.label);
    const isLeaf = index === segments.length - 1;
    if (entry.kind === "tab" && !entry.isActive?.()) {
      entry.activate?.();
    }
    if (!isLeaf) {
      continue;
    }
    const element = entry.getElement();
    if (element) {
      flashElement(element);
    }
    if (entry.kind === "action") {
      entry.activate?.();
    }
  }
  return { ok: true, labels };
}
